import React from 'react';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Atom, AtomRenderProps} from '@packages/systems/wfdl';
import {noPseudoYouTubeStyle, emptyYouTubeStyle} from './styles';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {EvaluatedNodeData, getInPojo} from '@packages/utilities/data-utils';
import {
  type RichTextValue,
  type VideoBindingProps,
} from '@packages/systems/dynamo/binding-props';

// eslint-disable-next-line webflow/module-top-level-imports
import {withLlmRender} from '@packages/utilities/llm-wfdl-renderer/withLlmRender';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';

const createMarkup = (value: undefined) => {
  return value ? {__html: value} : null;
};

const aspectRatio = ({height, width}: Record<any, any>) => {
  return height && width ? height / width : 0;
};

const optionKeyMap = {
  showAllRelatedVideos: 'rel',
  controls: 'controls',
  autoplay: 'autoplay',
  muted: 'mute',
  startAt: 'start',
} as const;

const aspectRatioPath = ['embed', 'meta', 'aspectRatio'];
const dataEmbedOptionsPath = ['embed', 'options'];
const dataEmbedMetaVideoIdPath = ['embed', 'meta', 'videoId'];
const dataEmbedMetaVideoTitlePath = ['embed', 'meta', 'title'];

const stylePaddingTopPath = ['style', 'padding-top'];

const getPaddingTop = (
  innerHTML: RichTextValue | VideoBindingProps | undefined,
  data: EvaluatedNodeData,
  fallbackPaddingTop: unknown
) => {
  const aspectRatioNodeData = getInPojo(data, aspectRatioPath);
  if (aspectRatioNodeData && aspectRatioNodeData !== 0)
    // @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
    return String(aspectRatioNodeData * 100) + '%';

  // @ts-expect-error - TS2339 - Property 'metadata' does not exist on type 'List<JSONNode> | JSONNode[] | VideoBindingProps'.
  return innerHTML && typeof innerHTML !== 'string' && innerHTML.metadata
    ? // @ts-expect-error - TS2339 - Property 'metadata' does not exist on type 'List<JSONNode> | JSONNode[] | VideoBindingProps'.
      `${aspectRatio(innerHTML.metadata) * 100}%`
    : fallbackPaddingTop;
};

const YouTubeVideo = Atom.createRender(
  withLlmRender(
    ({
      attributes,
      classes,
      data,
      generateAttributes,
      innerHTML,
    }: AtomRenderProps) => {
      let value;

      // padding top magic to enable resizing
      const paddingTop = getPaddingTop(
        innerHTML,
        data,
        attributes.getIn(stylePaddingTopPath)
      );
      attributes = attributes.setIn(stylePaddingTopPath, paddingTop);
      const videoId = getInPojo(data, dataEmbedMetaVideoIdPath);
      const title = getInPojo(data, dataEmbedMetaVideoTitlePath);

      // @ts-expect-error videoId
      if (!videoId || videoId.length === 0) {
        return (
          <div
            {...generateAttributes(attributes, classes)}
            // @ts-expect-error - createmarkup return is wack
            dangerouslySetInnerHTML={createMarkup(value)}
          />
        );
      }

      const options = getInPojo(data, dataEmbedOptionsPath);

      const optionsObj = {
        showAllRelatedVideos: getInPojo(options, ['showAllRelatedVideos']),
        controls: getInPojo(options, ['controls']),
        autoplay: getInPojo(options, ['autoplay']),
        muted: getInPojo(options, ['muted']),
        startAt: getInPojo(options, ['startAt']),
      } as const;

      const style = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto',
      } as const;

      const isPreviewMode = usePreviewMode();
      const runtimeEnvironment = useRuntimeEnvironment();

      // if in Designer disable autoplay and remove pointer-events from the iframe
      if (
        !isPreviewMode &&
        runtimeEnvironment === WebflowEnvironments.DESIGNER
      ) {
        // @ts-expect-error - TS2540 - Cannot assign to 'autoplay' because it is a read-only property.
        optionsObj.autoplay = 0;
        // @ts-expect-error - TS2540 - Cannot assign to 'pointerEvents' because it is a read-only property.
        style.pointerEvents = 'none';
      }

      const urlParams = Object.keys(optionsObj)
        .map(
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly showAllRelatedVideos: "rel"; readonly controls: "controls"; readonly autoplay: "autoplay"; readonly muted: "mute"; readonly startAt: "start"; }'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly showAllRelatedVideos: any; readonly controls: any; readonly autoplay: any; readonly muted: any; readonly startAt: any; }'.
          (option) => `${optionKeyMap[option]}=${Number(optionsObj[option])}`
        )
        .join('&');

      const baseUrl = getInPojo(options, ['privacyMode'])
        ? 'https://www.youtube-nocookie.com/embed'
        : 'https://www.youtube.com/embed';
      return (
        <div {...generateAttributes(attributes, classes)}>
          <iframe
            src={`${baseUrl}/${videoId}?${urlParams}`}
            frameBorder="0"
            style={style}
            allow="autoplay; encrypted-media"
            allowFullScreen
            // @ts-expect-error - title is undefined
            title={title}
          />
        </div>
      );
    }
  )
);

const config = Atom.create({
  displayName: 'YouTube',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Embed'), iconName('YouTube')),
  },
  showEmpty: false,
  style: {
    base: {
      main: {
        noPseudo: noPseudoYouTubeStyle,
        empty: emptyYouTubeStyle,
      },
    },
  },
  lockedStyles: {
    paddingTop: {
      when: true,
      message:
        'The top padding is automatically calculated in order to make this video responsive.',
    },
  },
  render: YouTubeVideo,
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('Embed'), typeName('YouTubeVideo'))
  ),
});

export default config;
